import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Database,
  ChevronDown,
  X,
} from 'lucide-react';
import { eventsApi, dataIngestionApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface SheetInfo {
  sheetName: string;
  headers: string[];
  rowCount: number;
  detectedTypes: Record<string, string>;
  existingSchema: { name: string; slug: string; recordCount: number } | null;
  headersMismatch: boolean;
}

interface IngestionResult {
  inserted: number;
  skipped: number;
  total: number;
  schemaName: string;
  schemaSlug: string;
  mode: string;
}

interface Event {
  _id: string;
  name: string;
  eventId: string;
}

export default function DataIngestion() {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<SheetInfo[]>([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [mode, setMode] = useState<'replace' | 'incremental'>('replace');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<IngestionResult | null>(null);

  const [loadingEvents, setLoadingEvents] = useState(false);
  const [preflight, setPreflight] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoadingEvents(true);
    try {
      const res = await eventsApi.getAll({ limit: 100 });
      setEvents((res.data?.data as any) || []);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setSheets([]);
    setSelectedSheet('');
    setResult(null);

    if (!selectedEventId) {
      toast.error('Please select an event first');
      return;
    }

    await runPreflight(f, selectedEventId);
  }

  async function runPreflight(f: File, eventId: string) {
    setPreflight(true);
    try {
      const res = await dataIngestionApi.preflight(f, eventId);
      const data = res.data?.data;
      if (!data?.sheets?.length) {
        toast.error('No readable sheets found in the file');
        return;
      }
      setSheets(data.sheets);
      setSelectedSheet(data.sheets[0].sheetName);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Pre-flight check failed');
    } finally {
      setPreflight(false);
    }
  }

  async function handleIngest() {
    if (!file || !selectedEventId || !selectedSheet) {
      toast.error('Please select an event, file, and sheet');
      return;
    }

    const sheet = sheets.find((s) => s.sheetName === selectedSheet);
    if (sheet?.headersMismatch && mode === 'incremental') {
      toast.error(`Column structure mismatch. Use Replace mode or fix the file.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setResult(null);

    try {
      const res = await dataIngestionApi.ingest(file, selectedEventId, selectedSheet, mode, setUploadProgress);
      const data = res.data?.data;
      if (data) {
        setResult(data);
        toast.success(`Ingestion complete: ${data.inserted} records inserted`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Ingestion failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  function handleReset() {
    setFile(null);
    setSheets([]);
    setSelectedSheet('');
    setResult(null);
    setMode('replace');
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const currentSheet = sheets.find((s) => s.sheetName === selectedSheet);
  const showModeToggle = !!currentSheet?.existingSchema;
  const hasMismatch = currentSheet?.headersMismatch && mode === 'incremental';

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Data Ingestion</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload structured data files (xlsx, csv) to make them queryable by the chatbot.
        </p>
      </div>

      {/* Event selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
        <div className="relative">
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedEventId}
            onChange={(e) => {
              setSelectedEventId(e.target.value);
              setFile(null);
              setSheets([]);
              setSelectedSheet('');
              setResult(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            disabled={loadingEvents}
          >
            <option value="">Select an event...</option>
            {events.map((ev) => (
              <option key={ev._id} value={ev._id}>
                {ev.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* File picker */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            !selectedEventId
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onClick={() => selectedEventId && fileInputRef.current?.click()}
        >
          {preflight ? (
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">Analysing file...</span>
            </div>
          ) : file ? (
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">{file.name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); handleReset(); }}
                className="ml-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <Upload className="w-8 h-8" />
              <span className="text-sm">
                {selectedEventId ? 'Click to select xlsx, csv, or tsv file' : 'Select an event first'}
              </span>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv,.tsv"
          className="hidden"
          onChange={handleFileChange}
          disabled={!selectedEventId}
        />
      </div>

      {/* Sheet selector (xlsx only, when multiple sheets) */}
      {sheets.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sheet</label>
          <div className="relative">
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedSheet}
              onChange={(e) => setSelectedSheet(e.target.value)}
            >
              {sheets.map((s) => (
                <option key={s.sheetName} value={s.sheetName}>
                  {s.sheetName} ({s.rowCount} rows)
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* Sheet info + existing schema notice */}
      {currentSheet && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
          <div className="flex items-start gap-2">
            <Database className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-700">
                <span className="font-medium">{currentSheet.rowCount}</span> rows ·{' '}
                <span className="font-medium">{currentSheet.headers.length}</span> columns
              </p>
              {currentSheet.existingSchema && (
                <p className="text-amber-700 mt-1">
                  Existing dataset found: <span className="font-medium">{currentSheet.existingSchema.name}</span>
                  {' '}({currentSheet.existingSchema.recordCount} records)
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mode toggle (only when existing schema found) */}
      {showModeToggle && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Upload Mode</label>
          <div className="flex gap-3">
            <button
              onClick={() => setMode('replace')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                mode === 'replace'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Replace all existing data
            </button>
            <button
              onClick={() => setMode('incremental')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                mode === 'incremental'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Add incremental data
            </button>
          </div>
        </div>
      )}

      {/* Header mismatch warning */}
      {hasMismatch && (
        <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Column structure doesn't match the existing dataset. Switch to <strong>Replace</strong> mode or fix the file.
          </span>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Result summary */}
      {result && (
        <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">Ingestion complete — {result.schemaName}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 text-center border border-green-200">
              <p className="text-2xl font-semibold text-gray-900">{result.inserted}</p>
              <p className="text-gray-500">Records inserted</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-green-200">
              <p className="text-2xl font-semibold text-gray-900">{result.skipped}</p>
              <p className="text-gray-500">Duplicates skipped</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center border border-green-200">
              <p className="text-2xl font-semibold text-gray-900">{result.total}</p>
              <p className="text-gray-500">Total rows</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!file && !result && (
        <div className="mb-4 p-6 text-center text-sm text-gray-400 border border-gray-200 rounded-lg bg-gray-50">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>Select an event and upload a structured data file to get started.</p>
          <p className="mt-1">Supported formats: <span className="font-medium">.xlsx, .xls, .csv, .tsv</span></p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleIngest}
          disabled={!file || !selectedEventId || !selectedSheet || uploading || hasMismatch}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Ingesting...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Ingest Data
            </>
          )}
        </button>
        {(file || result) && (
          <button
            onClick={handleReset}
            disabled={uploading}
            className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
